import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Eye, EyeOff, PlayCircle, ArrowLeft,
  ShieldCheck, FileSearch, BarChart3, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import "./login-page.css";

const DEMO_ROLES = [
  { code: "super_admin", label: "Super Admin Demo" },
  { code: "direktur_utama", label: "Direktur Demo" },
  { code: "keuangan", label: "Keuangan Demo" },
  { code: "akuntansi", label: "Akuntansi Demo" },
  { code: "perpajakan", label: "Perpajakan Demo" },
  { code: "auditor", label: "Auditor Demo" },
  { code: "viewer", label: "Pengguna Lihat Saja Demo" },
];

const FEATURES = [
  { icon: ShieldCheck, label: "Kontrol akses berbasis peran" },
  { icon: FileSearch,  label: "Jejak audit lengkap" },
  { icon: BarChart3,   label: "Laporan real-time" },
  { icon: Receipt,     label: "Integrasi perpajakan" },
];

export default function LoginPage() {
  const { user, login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoRole, setDemoRole] = useState("super_admin");
  const [demoEnabled, setDemoEnabled] = useState(false);

  React.useEffect(() => {
    api.get("/auth/demo-status").then(({ data }) => setDemoEnabled(!!data.enabled)).catch(() => setDemoEnabled(false));
  }, []);

  React.useEffect(() => {
    if (user && user !== false) navigate("/dasbor", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(identifier, password, remember);
    setLoading(false);
    if (res.ok) {
      toast.success("Selamat datang. Anda berhasil masuk ke sistem.");
      navigate("/dasbor");
    } else {
      toast.error(res.error);
    }
  };

  const handleDemo = async () => {
    setLoading(true);
    const res = await demoLogin(demoRole);
    setLoading(false);
    if (res.ok) {
      toast.success("Anda memasuki Mode Demo.");
      navigate("/dasbor");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="abs-login" data-testid="login-page">
      {/* Backdrop image + overlays */}
      <div className="abs-bg" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=2400&q=80"
          alt=""
          className="abs-bg__img"
        />
        <div className="abs-bg__overlay" />
        <div className="abs-bg__vignette" />
        <div className="abs-bg__glow" />
      </div>

      {/* Bottom red/gold wave */}
      <svg
        className="abs-wave" viewBox="0 0 1440 260" preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="waveGold" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#F5C84C" />
            <stop offset="1" stopColor="#E9B640" />
          </linearGradient>
          <linearGradient id="waveRed" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#720B16" />
            <stop offset="0.55" stopColor="#A10F1A" />
            <stop offset="1" stopColor="#720B16" />
          </linearGradient>
        </defs>
        <path
          d="M0,150 C 240,60 520,240 760,160 C 1000,80 1240,220 1440,120 L1440,260 L0,260 Z"
          fill="url(#waveGold)" opacity="0.95"
        />
        <path
          d="M0,190 C 260,110 540,270 800,200 C 1040,140 1260,250 1440,170 L1440,260 L0,260 Z"
          fill="url(#waveRed)"
        />
      </svg>

      {/* Foreground */}
      <div className="abs-stage">
        {/* Brand header (top-left) */}
        <header className="abs-brand">
          <img
            src="/assets/logo-abs.png" alt="PT Amaydelli Berkah Sejahtera"
            className="abs-brand__logo"
          />
          <div className="abs-brand__text">
            <div className="abs-brand__company">PT Amaydelli Berkah Sejahtera</div>
            <div className="abs-brand__system">
              ABS Finance, Accounting, Tax &amp; Admin
            </div>
            <span className="abs-brand__rule" aria-hidden="true" />
          </div>
        </header>

        {/* Hero — left column */}
        <section className="abs-hero">
          <h1 className="abs-hero__title">
            <span className="abs-hero__title--white">Building Brands,</span>
            <span className="abs-hero__title--gold">Creating Growth</span>
          </h1>

          <p className="abs-hero__desc">
            Membangun brand yang kuat, kompetitif, dan berkelanjutan melalui
            inovasi produk, pemasaran, distribusi, serta kemitraan strategis.
          </p>

          <ul className="abs-features" data-testid="feature-list">
            {FEATURES.map(({ icon: Icon, label }) => (
              <li className="abs-feature" key={label}>
                <span className="abs-feature__icon"><Icon strokeWidth={1.8} /></span>
                <span className="abs-feature__label">{label}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Right column — Ciamay logo + login card */}
        <aside className="abs-right">
          <div className="abs-ciamay">
            <div className="abs-ciamay__halo" aria-hidden="true" />
            <img
              src="/assets/logo-ciamay.png" alt="Ciamay"
              className="abs-ciamay__img"
            />
          </div>

          <div className="abs-card" data-testid="login-card">
            {!demoOpen ? (
              <>
                <h2 className="abs-card__title">Masuk ke Sistem</h2>
                <p className="abs-card__sub">
                  Gunakan akun Anda untuk mengakses aplikasi.
                </p>

                <form
                  onSubmit={handleSubmit} className="abs-form"
                  data-testid="login-form"
                >
                  <div className="abs-field">
                    <Label htmlFor="identifier">Email atau Nama Pengguna</Label>
                    <Input
                      id="identifier" autoComplete="username" value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder="admin@abs.co.id" required
                      data-testid="login-identifier-input"
                    />
                  </div>

                  <div className="abs-field">
                    <Label htmlFor="password">Kata Sandi</Label>
                    <div className="abs-pw">
                      <Input
                        id="password" type={showPw ? "text" : "password"}
                        autoComplete="current-password" value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Masukkan kata sandi Anda" required
                        data-testid="login-password-input"
                      />
                      <button
                        type="button" onClick={() => setShowPw((v) => !v)}
                        className="abs-pw__toggle"
                        aria-label={showPw ? "Sembunyikan Kata Sandi" : "Tampilkan Kata Sandi"}
                        data-testid="login-toggle-password"
                      >
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="abs-row">
                    <label className="abs-remember">
                      <Checkbox
                        checked={remember}
                        onCheckedChange={(v) => setRemember(!!v)}
                        data-testid="login-remember-checkbox"
                      />
                      Ingat Saya
                    </label>
                    <button
                      type="button" className="abs-forgot"
                      data-testid="login-forgot-link"
                    >
                      Lupa Kata Sandi?
                    </button>
                  </div>

                  <Button
                    type="submit" disabled={loading}
                    className="abs-btn abs-btn--primary"
                    data-testid="login-submit-button"
                  >
                    {loading ? "Memuat data..." : "Masuk"}
                  </Button>

                  <div className="abs-sep"><span>atau</span></div>

                  <Button
                    type="button" variant="outline"
                    onClick={() => setDemoOpen(true)}
                    disabled={!demoEnabled}
                    className="abs-btn abs-btn--ghost"
                    data-testid="login-demo-button"
                  >
                    <PlayCircle size={16} style={{ marginRight: 8 }} />
                    Masuk ke Mode Demo
                  </Button>
                  {!demoEnabled && (
                    <p className="text-xs text-slate-500 text-center mt-2" data-testid="login-demo-disabled-note">Mode Demo sedang disiapkan.</p>
                  )}
                </form>
              </>
            ) : (
              <>
                <button
                  onClick={() => setDemoOpen(false)} className="abs-back"
                  data-testid="demo-back-button"
                >
                  <ArrowLeft size={16} /> Kembali
                </button>
                <h2 className="abs-card__title">Mode Demo</h2>
                <p className="abs-card__sub">
                  Pilih peran demo untuk menjelajahi aplikasi. Data yang tampil
                  adalah simulasi.
                </p>
                <div className="abs-field">
                  <Label>Pilih Peran Demo</Label>
                  <Select value={demoRole} onValueChange={setDemoRole}>
                    <SelectTrigger data-testid="demo-role-select">
                      <SelectValue placeholder="Pilih peran demo" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEMO_ROLES.map((r) => (
                        <SelectItem
                          key={r.code} value={r.code}
                          data-testid={`demo-role-option-${r.code}`}
                        >
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="abs-warn">
                  <strong>MODE DEMO</strong> — Data pada halaman ini hanya data simulasi.
                </div>
                <Button
                  onClick={handleDemo} disabled={loading}
                  className="abs-btn abs-btn--primary"
                  data-testid="demo-submit-button"
                >
                  {loading ? "Memuat data..." : "Masuk sebagai Demo"}
                </Button>
              </>
            )}
          </div>
        </aside>

        {/* Footer */}
        <footer className="abs-foot">© 2026 Amaydelli</footer>
      </div>
    </div>
  );
}
